import React, { useEffect, useState } from 'react';
import { css, Theme } from '@emotion/react';
import { Toggle } from '@birdi/toggle';
import { breakpoints } from '@birdi/theme';
import ChevronDownSVG from '@birdi/icons/chevron-down.svg';

export enum PermissionNames {
  CanAddEditAndRemoveUser = 'Can add, edit and remove users',
  CanUpdateUserPermissions = 'Can update user permissions',
  CanInviteGuests = 'Can invite guests',
  // CanShareMaps = 'Can share maps',
  CanUpdateBranding = 'Can update branding',
  CanAccessAccountInformation = 'Can access account information',
  CanUpgradeDowngradePlans = 'Can upgrade/downgrade plans',
  CanAddStorage = 'Can add storage',
  CanAddSeats = 'Can add seats',
  CanRequestProcessing = 'Can request processing',
  CanCreateWorkspaces = 'Can create workspaces',
  CanDeleteWorkspaces = 'Can delete workspaces',
  CanAddEditAndRemoveUsersFromWorkspaces = 'Can add, edit and remove users from workspaces',
  CanCreateUpdateAndManageMaps = 'Can create, update and manage maps',
  CanViewAllMaps = 'Can view all maps',
  CanUploadAndCreateLayersInMaps = 'Can upload & create layers in maps',
  CanDownloadFromMaps = 'Can download from maps',
  CanCommentOnMaps = 'Can comment on maps',
  CanShareMapsWithWorkspaceUsers = 'Can share maps with workspace users',
  CanShareMapsWithGuestsViewOnly = 'Can share maps with guests (view only)',
  CanDeleteMaps = 'Can delete maps',
  CanCreateSites = 'Can create sites',
  CanViewAllWorkspaceMaps = 'Can view all workspace maps',
  CanToggleLayerVisibility = 'Can toggle layer visibility',
}

const generalOptions = [
  PermissionNames.CanAddEditAndRemoveUser,
  PermissionNames.CanUpdateUserPermissions,
  PermissionNames.CanInviteGuests,
  PermissionNames.CanUpdateBranding,
];
const billingOptions = [
  PermissionNames.CanAccessAccountInformation,
  PermissionNames.CanUpgradeDowngradePlans,
  PermissionNames.CanAddStorage,
  PermissionNames.CanAddSeats,
  PermissionNames.CanRequestProcessing,
];
const workspaceOptions = [
  PermissionNames.CanCreateWorkspaces,
  PermissionNames.CanDeleteWorkspaces,
  PermissionNames.CanAddEditAndRemoveUsersFromWorkspaces,
];
const mapOptions = [
  PermissionNames.CanCreateUpdateAndManageMaps,
  PermissionNames.CanViewAllMaps,
  PermissionNames.CanUploadAndCreateLayersInMaps,
  PermissionNames.CanDownloadFromMaps,
  PermissionNames.CanCommentOnMaps,
  PermissionNames.CanShareMapsWithWorkspaceUsers,
  PermissionNames.CanShareMapsWithGuestsViewOnly,
  PermissionNames.CanDeleteMaps,
  PermissionNames.CanCreateSites,
  PermissionNames.CanViewAllWorkspaceMaps,
  PermissionNames.CanToggleLayerVisibility,
];

const allOptions = [
  {
    title: 'General',
    value: generalOptions,
  },
  {
    title: 'Billing & Subscription',
    value: billingOptions,
  },
  {
    title: 'Workspaces',
    value: workspaceOptions,
  },
  {
    title: 'Maps',
    value: mapOptions,
  },
];

const rolePermissions = {
  'Organisation Admin': [
    ...generalOptions,
    ...billingOptions,
    ...workspaceOptions,
    ...mapOptions,
  ],
  'Workspace Admin': [
    ...generalOptions,
    PermissionNames.CanRequestProcessing,
    PermissionNames.CanAddEditAndRemoveUsersFromWorkspaces,
    ...mapOptions,
  ],
  'Workspace User': [...mapOptions],
  Guest: [
    PermissionNames.CanDownloadFromMaps,
    PermissionNames.CanCommentOnMaps,
    PermissionNames.CanViewAllWorkspaceMaps,
    PermissionNames.CanToggleLayerVisibility,
  ],
};

interface Props {
  title: string;
  role: string;
}

const PermissionSelect = ({ title, role }: Props) => (
  <fieldset
    css={css`
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      border: none;
      padding: 0.5em 1em;
      label {
        margin-right: 1em;
      }
    `}
  >
    <label>{title}</label>
    <Toggle
      checked={
        rolePermissions[role] ? rolePermissions[role].includes(title) : false
      }
      onChange={() => null}
      // disabled
    />
  </fieldset>
);

export const PermissionsComponent = ({ role }) => {
  // useEffect(() => {
  //   console.log('role: ', role);
  // }, [role]);

  return (
    <div
      css={(theme) => css`
        border: solid 1px ${theme.mono30};
        border-radius: 10px;
        max-width: 30em;
        @media (max-width: ${breakpoints.xs}) {
          max-width: 100%;
        }
      `}
    >
      <ul
        css={css`
          padding: 0;
        `}
      >
        {allOptions.map((option) => (
          <div key={option.title}>
            <div
              css={(theme) => css`
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                border-bottom: solid 1px ${theme.mono30};
                padding: 1.25em;
                padding-top: ${option.title === 'General' ? '0.5em' : '1.25em'};
              `}
            >
              <span
                css={css`
                  font-weight: bold;
                `}
              >
                {option.title}
              </span>
              <ChevronDownSVG />
            </div>
            {option.value.map((item) => (
              <PermissionSelect key={item} title={item} role={role} />
            ))}
          </div>
        ))}
      </ul>
    </div>
  );
};
